import type {
  ReceiverConfig,
  HandleIncomingOptions,
  AuthToken,
  UserInfo,
  ChangeAppSession,
  RenewAppTokenBody,
} from './types';
import {
  MINITOKEN_PARAM,
  ORIGIN_CLIENT_ID_PARAM,
  USER_ID_PARAM,
} from './constants';
import { httpPost, httpGet } from './http';

/**
 * ChangeAppReceiver
 *
 * Handles inbound cross-portal navigation on the receiving side.
 * Extracts the minitoken from the URL and exchanges it through the BFF for
 * an access token for this portal.
 *
 * ## Flow
 * 1. Checks URL for `?minitoken=...` parameter
 * 2. Calls BFF `POST /auth/renew-app-token` with the minitoken
 *    (BFF → IAM `POST /user-identities/renew-app-token`)
 * 3. Calls BFF `GET /auth/user-info` with the new token
 * 4. Cleans the minitoken from the URL
 *
 * @example
 * ```ts
 * if (ChangeAppReceiver.hasMinitoken()) {
 *   const receiver = new ChangeAppReceiver({
 *     bffBaseUrl: 'https://api.example.com',
 *   });
 *
 *   const session = await receiver.handleIncoming({
 *     clientId: 'this-portal-client-id',
 *     originClientId: 'origin-portal-client-id',
 *     userId: 'user-123',
 *     rememberMe: true,
 *   });
 * }
 * ```
 *
 * @since 1.0.0
 * @category Services/ChangeApp
 */
export class ChangeAppReceiver {
  private readonly bffBaseUrl: string;

  private lastAuthToken: AuthToken | null = null;

  constructor(config: ReceiverConfig) {
    this.bffBaseUrl = config.bffBaseUrl.replace(/\/+$/, '');
  }

  /**
   * Check if the current URL contains a minitoken
   *
   * @returns True if a minitoken query parameter is present
   */
  static hasMinitoken(): boolean {
    return ChangeAppReceiver.extractMinitoken() !== null;
  }

  /**
   * Extract the minitoken from the current URL
   *
   * @returns The minitoken string, or null if not present
   */
  static extractMinitoken(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(MINITOKEN_PARAM) || null;
  }

  /**
   * Extract the origin client ID from the current URL
   *
   * @returns The origin client ID string, or null if not present
   */
  static extractOriginClientId(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(ORIGIN_CLIENT_ID_PARAM) || null;
  }

  /**
   * Extract the user ID from the current URL
   *
   * @returns The user ID string, or null if not present
   */
  static extractUserId(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(USER_ID_PARAM) || null;
  }

  /**
   * Handle an incoming cross-portal navigation
   *
   * Exchanges the minitoken for an access token, fetches user info, and
   * cleans the URL.
   *
   * @param options - Options for token exchange
   * @returns Session metadata and user info
   * @throws {ChangeAppError} When the BFF/IAM returns an error
   * @throws {Error} When no minitoken is found in the URL
   */
  async handleIncoming(
    options: HandleIncomingOptions
  ): Promise<ChangeAppSession> {
    const minitoken = ChangeAppReceiver.extractMinitoken();
    if (!minitoken) {
      throw new Error('No minitoken found in URL query parameters.');
    }

    const auth = await this.renewAppToken(minitoken, options);
    this.lastAuthToken = auth;

    const user = await this.getUserInfo(auth.accessToken);

    this.cleanUrl();

    return { auth, user };
  }

  /**
   * Exchange the URL minitoken for an access token without fetching user
   * info. The minitoken params are always stripped from the URL.
   *
   * @param options - Options for token exchange
   * @returns Token data from the BFF
   * @throws {ChangeAppError} When the BFF/IAM returns an error
   * @throws {Error} When no minitoken is found in the URL
   */
  async renewFromUrl(options: HandleIncomingOptions): Promise<AuthToken> {
    const minitoken = ChangeAppReceiver.extractMinitoken();
    if (!minitoken) {
      throw new Error('No minitoken found in URL query parameters.');
    }

    try {
      const auth = await this.renewAppToken(minitoken, options);
      this.lastAuthToken = auth;
      return auth;
    } finally {
      // Always strip the token params: the minitoken is single-use, so on
      // failure keeping it in the URL/history would only replay a burned
      // token on refresh.
      this.cleanUrl();
    }
  }

  /**
   * Fetch user info for the session established by a previous exchange
   *
   * @returns User information
   * @throws {Error} When no exchange has happened yet
   */
  async fetchUserInfo(): Promise<UserInfo> {
    if (!this.lastAuthToken) {
      throw new Error(
        'No session available. Call handleIncoming() or renewFromUrl() first.'
      );
    }
    return this.getUserInfo(this.lastAuthToken.accessToken);
  }

  /**
   * Exchange a minitoken for an access token via the BFF
   */
  private async renewAppToken(
    minitoken: string,
    options: HandleIncomingOptions
  ): Promise<AuthToken> {
    const body: RenewAppTokenBody = {
      minitoken,
      clientId: options.clientId,
      rememberMe: options.rememberMe,
      ...(options.originClientId && { originClientId: options.originClientId }),
      ...(options.userId && { userId: options.userId }),
      ...(options.ip && { ip: options.ip }),
    };

    const headers: Record<string, string> = {};
    if (options.deviceFingerprint) {
      headers['deviceFingerprint'] = options.deviceFingerprint;
    }

    return httpPost<AuthToken>(
      `${this.bffBaseUrl}/auth/renew-app-token`,
      body as unknown as Record<string, unknown>,
      headers
    );
  }

  /**
   * Fetch user info from the BFF with the given access token
   */
  private async getUserInfo(accessToken: string): Promise<UserInfo> {
    return httpGet<UserInfo>(`${this.bffBaseUrl}/auth/user-info`, {
      Authorization: `Bearer ${accessToken}`,
    });
  }

  /**
   * Remove minitoken and related params from the URL
   */
  private cleanUrl(): void {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete(MINITOKEN_PARAM);
    url.searchParams.delete(ORIGIN_CLIENT_ID_PARAM);
    url.searchParams.delete(USER_ID_PARAM);

    window.history.replaceState(window.history.state, '', url.toString());
  }
}
